import React, { forwardRef, useImperativeHandle, useMemo } from 'react';

import { Outlet } from 'react-router-dom';
import cx from 'classnames';

import { ConfigProvider, Tabs } from '@/widgets';
import themeConfig from '@/theme/themeConfig';

import styles from './Navtabs.module.scss';
import { useNavtabs } from './useNavtabs';
import Navtab from './Navtab';

/**
 * @typedef { Object } NavTabsProps
 * @property { string } [baseHref] baseUrl to prepend on all routes
 * @property { React.ReactNode } [loader] - view to show while route is loading
 * @property { import('./Navtab').Navtab[] } tabs
 */

/**
 * @description Tabs component for navigation
 *
 * @author  Rohit Niraj Singh <@rohit.singh3>
 *
 * @param { import('../Tabs').CoreTabsProps & NavTabsProps } props
 *
 *
 * @returns {React.Component}
 */

const Navtabs = forwardRef(
	({ extraRightContent, tabs, baseHref = '', onTabChange }, ref) => {
		const { activeTab } = useNavtabs({ tabs, baseHref, onTabChange });

		const customTabs = useMemo(() => {
			return tabs.map((tab) => ({
				...tab,
				link: baseHref + tab.link,
				label: (
					<Navtab
						isActive={activeTab === tab.key}
						link={baseHref + tab.link}
						label={tab.label}
					/>
				),
			}));
		}, [tabs, baseHref, activeTab]);

		useImperativeHandle(
			ref,
			() => ({
				activeTab,
			}),
			[activeTab],
		);

		return (
			<div className={cx(styles.navigationTabs)}>
				<ConfigProvider
					theme={{
						token: {
							colorPrimary: themeConfig['secondary_new'],
						},
					}}
				>
					<Tabs
						direction="horizontal"
						extraRightContent={extraRightContent}
						onTabChange={onTabChange}
						activeKey={activeTab}
						tabs={customTabs}
					/>
				</ConfigProvider>
				<Outlet />
			</div>
		);
	},
);

Navtabs.displayName = 'Navtabs';

export default Navtabs;
